import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api';
import { formatMoney, currencyCode } from '../utils/currency';

const EXPENSE_CATEGORIES = [
  'Feed',
  'Veterinary',
  'Medicine',
  'Labor',
  'Transport',
  'Breeding',
  'Equipment',
  'Repairs',
  'Utilities',
  'Other',
];

const INCOME_CATEGORIES = [
  'Sheep Sale',
  'Wool',
  'Breeding Service',
  'Manure',
  'Other',
];

const COLORS = ['#2f7d4c', '#2f6f9f', '#b54735', '#9a6700', '#6b7280', '#8ca98a'];
const RANGE_OPTIONS = [
  { key: 'month', label: 'Month' },
  { key: '3m', label: '3 Months', months: 3 },
  { key: '6m', label: '6 Months', months: 6 },
  { key: '12m', label: '1 Year', months: 12 },
  { key: 'all', label: 'All' },
];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const today = () => new Date().toISOString().slice(0, 10);
const monthToDate = (month) => {
  const [year, monthIndex] = month.split('-').map(Number);
  return new Date(year, monthIndex - 1, 1);
};
const dateToMonth = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const addMonths = (month, amount) => {
  const date = monthToDate(month);
  date.setMonth(date.getMonth() + amount);
  return dateToMonth(date);
};
const formatMonthLabel = (month, includeYear = true) => {
  const [year, monthIndex] = month.split('-').map(Number);
  return `${MONTH_NAMES[monthIndex - 1]}${includeYear ? ` ${year}` : ''}`;
};
const monthsBetween = (start, end) => {
  const months = [];
  let cursor = monthToDate(start);
  const stop = monthToDate(end);
  while (cursor <= stop) {
    months.push(dateToMonth(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};
const getTransactionMonth = (transaction) => (transaction.date || '').slice(0, 7);
const rangeDetails = (range, selectedMonth, transactions) => {
  if (range === 'month') {
    return {
      start: selectedMonth,
      end: selectedMonth,
      label: formatMonthLabel(selectedMonth),
      emptyLabel: formatMonthLabel(selectedMonth),
    };
  }

  if (range === 'all') {
    const transactionMonths = transactions.map(getTransactionMonth).filter(Boolean).sort();
    return {
      start: transactionMonths[0] || currentMonth(),
      end: transactionMonths[transactionMonths.length - 1] || currentMonth(),
      label: 'All Time',
      emptyLabel: 'all time',
      isAll: true,
    };
  }

  const option = RANGE_OPTIONS.find(item => item.key === range);
  const end = currentMonth();
  return {
    start: addMonths(end, -(option.months - 1)),
    end,
    label: `Last ${option.label}`,
    emptyLabel: `the last ${option.label.toLowerCase()}`,
  };
};
const inRange = (transaction, details) => {
  if (details.isAll) return true;
  const month = getTransactionMonth(transaction);
  return month >= details.start && month <= details.end;
};
const buildMonthlySeries = (transactions, details) => {
  const monthKeys = monthsBetween(details.start, details.end);
  const byMonth = monthKeys.reduce((acc, month) => {
    acc[month] = { month, income: 0, expenses: 0, profit: 0, transaction_count: 0 };
    return acc;
  }, {});

  transactions.forEach(transaction => {
    const month = getTransactionMonth(transaction);
    if (!byMonth[month]) return;
    const amount = Number(transaction.amount || 0);
    if (transaction.transaction_type === 'income') byMonth[month].income += amount;
    else byMonth[month].expenses += amount;
    byMonth[month].transaction_count += 1;
  });

  return monthKeys.map(month => ({
    ...byMonth[month],
    profit: byMonth[month].income - byMonth[month].expenses,
    label: formatMonthLabel(month, monthKeys.length > 6),
  }));
};
const groupExpenseCategories = (transactions) => {
  const totals = transactions
    .filter(transaction => transaction.transaction_type === 'expense')
    .reduce((acc, transaction) => {
      const category = transaction.category || 'Other';
      acc[category] = (acc[category] || 0) + Number(transaction.amount || 0);
      return acc;
    }, {});
  return Object.entries(totals)
    .map(([category, total]) => ({ category, total }))
    .sort((a, b) => b.total - a.total);
};
const parseMoneyInput = (value) => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
// Per-animal economics use each sheep's actual attributable health cost
// (vaccination + treatment spend), not an arbitrary even split of farm expenses.
const buildFlockValuation = (sheep, ledgerTotals) => {
  const rows = sheep.map(item => {
    const purchasePrice = Number(item.purchase_price || 0);
    const salePrice = item.sale_price != null ? Number(item.sale_price) : null;
    const marketValue = item.market_value != null ? Number(item.market_value) : null;
    const usedValue = salePrice != null ? salePrice : marketValue;
    const grossProfit = usedValue != null ? usedValue - purchasePrice : null;
    const healthCost = Number(item.health_cost || 0);
    return {
      ...item,
      purchasePrice,
      salePrice,
      marketValue,
      usedValue,
      valueSource: salePrice != null ? 'Sale' : marketValue != null ? 'Market' : 'Missing',
      grossProfit,
      healthCost,
      netPosition: grossProfit != null ? grossProfit - healthCost : null,
    };
  });
  const activeRows = rows.filter(row => row.status === 'active');
  const estimatedValue = activeRows.reduce((sum, row) => sum + Number(row.marketValue || 0), 0);
  const purchaseCost = activeRows.reduce((sum, row) => sum + row.purchasePrice, 0);
  const healthCost = activeRows.reduce((sum, row) => sum + row.healthCost, 0);
  // Purchases already exist in ledger expenses. Using the ledger once prevents
  // acquisition and operating costs from being counted twice.
  const currentPosition = estimatedValue + ledgerTotals.income - ledgerTotals.expenses;
  return {
    rows,
    estimatedValue,
    purchaseCost,
    healthCost,
    totalIncome: ledgerTotals.income,
    totalExpenses: ledgerTotals.expenses,
    currentPosition,
    missingValues: activeRows.filter(row => row.marketValue == null).length,
  };
};

const emptyForm = () => ({
  transaction_type: 'expense',
  category: 'Feed',
  amount: '',
  date: today(),
  vendor: '',
  description: '',
  notes: '',
});

export default function Finance() {
  const [allTransactions, setAllTransactions] = useState([]);
  const [allSheep, setAllSheep] = useState([]);
  const [marketInputs, setMarketInputs] = useState({});
  const [savingMarketId, setSavingMarketId] = useState(null);
  const [valuationSearch, setValuationSearch] = useState('');
  const [range, setRange] = useState('month');
  const [month, setMonth] = useState(currentMonth());
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const categories = form.transaction_type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const load = () => {
    setError('');
    Promise.all([api.listTransactions(), api.listSheep()])
      .then(([ledger, sheep]) => {
        setAllTransactions(ledger);
        setAllSheep(sheep);
        setMarketInputs(Object.fromEntries(
          sheep.map(item => [item.id, item.market_value != null ? String(item.market_value) : ''])
        ));
      })
      .catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, []);

  const selectedRange = useMemo(
    () => rangeDetails(range, month, allTransactions),
    [range, month, allTransactions]
  );

  const transactions = useMemo(
    () => allTransactions
      .filter(transaction => inRange(transaction, selectedRange))
      .sort((a, b) => (b.date || '').localeCompare(a.date || '') || Number(b.id || 0) - Number(a.id || 0)),
    [allTransactions, selectedRange]
  );

  const monthTotals = useMemo(() => {
    const income = transactions.filter(t => t.transaction_type === 'income').reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const expenses = transactions.filter(t => t.transaction_type === 'expense').reduce((sum, t) => sum + Number(t.amount || 0), 0);
    return { income, expenses, profit: income - expenses };
  }, [transactions]);

  const monthlyData = useMemo(
    () => buildMonthlySeries(transactions, selectedRange),
    [transactions, selectedRange]
  );

  const categoriesData = useMemo(
    () => groupExpenseCategories(transactions),
    [transactions]
  );

  const allTimeTotals = useMemo(() => ({
    income: allTransactions
      .filter(t => t.transaction_type === 'income')
      .reduce((sum, t) => sum + Number(t.amount || 0), 0),
    expenses: allTransactions
      .filter(t => t.transaction_type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount || 0), 0),
  }), [allTransactions]);

  const valuation = useMemo(
    () => buildFlockValuation(allSheep, allTimeTotals),
    [allSheep, allTimeTotals]
  );

  const valuationRows = useMemo(() => {
    const value = valuationSearch.trim().toLowerCase();
    if (!value) return valuation.rows;
    return valuation.rows.filter(row => row.tag_id.toLowerCase().includes(value));
  }, [valuation.rows, valuationSearch]);

  const set = (key, value) => {
    setForm(f => {
      const next = { ...f, [key]: value };
      if (key === 'transaction_type') {
        next.category = value === 'income' ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0];
      }
      return next;
    });
  };

  const startAdd = (type = 'expense') => {
    setEditing(null);
    setForm({ ...emptyForm(), transaction_type: type, category: type === 'income' ? INCOME_CATEGORIES[0] : EXPENSE_CATEGORIES[0] });
    setShowForm(true);
  };

  const startEdit = (transaction) => {
    setEditing(transaction);
    setForm({
      transaction_type: transaction.transaction_type,
      category: transaction.category,
      amount: transaction.amount,
      date: transaction.date,
      vendor: transaction.vendor || '',
      description: transaction.description || '',
      notes: transaction.notes || '',
    });
    setShowForm(true);
  };

  const cancel = () => {
    setEditing(null);
    setShowForm(false);
    setForm(emptyForm());
  };

  const submit = async () => {
    setError('');
    setMessage('');
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      editing ? await api.updateTransaction(editing.id, payload) : await api.createTransaction(payload);
      setMessage(editing ? 'Transaction updated.' : 'Transaction logged.');
      cancel();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    setError('');
    setMessage('');
    try {
      await api.deleteTransaction(id);
      setMessage('Transaction deleted.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const saveMarketValue = async (sheep) => {
    const nextValue = parseMoneyInput(marketInputs[sheep.id]);
    if (marketInputs[sheep.id] !== '' && nextValue == null) {
      setError('Market value must be a valid number.');
      return;
    }
    setError('');
    setMessage('');
    setSavingMarketId(sheep.id);
    try {
      const updated = await api.updateSheep(sheep.id, { ...sheep, market_value: nextValue });
      setAllSheep(items => items.map(item => item.id === sheep.id ? updated : item));
      setMarketInputs(inputs => ({ ...inputs, [sheep.id]: updated.market_value != null ? String(updated.market_value) : '' }));
      setMessage(`Market value saved for ${sheep.tag_id}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMarketId(null);
    }
  };

  return (
    <div className="page finance-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Finance</h1>
          <p className="page-subtitle">Farm-wide expense, income, and profit tracking</p>
        </div>
        <div className="profile-actions">
          <button className="btn btn-primary" onClick={() => startAdd('expense')}>+ Expense</button>
          <button className="btn btn-secondary" onClick={() => startAdd('income')}>+ Income</button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}
      {message && <div className="alert alert-success mb-4">{message}</div>}

      <div className="finance-toolbar">
        <div className="range-toggle" role="group" aria-label="Finance period">
          {RANGE_OPTIONS.map(option => (
            <button
              key={option.key}
              type="button"
              className={range === option.key ? 'active' : ''}
              onClick={() => setRange(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {range === 'month' && (
          <input className="month-input" type="month" value={month} onChange={e => setMonth(e.target.value)} />
        )}
      </div>

      <div className="stat-grid finance-stat-grid">
        <div className="stat-card finance-card expense">
          <div className="stat-value">{formatMoney(monthTotals.expenses)}</div>
          <div className="stat-label">Expenses {selectedRange.label}</div>
        </div>
        <div className="stat-card finance-card income">
          <div className="stat-value">{formatMoney(monthTotals.income)}</div>
          <div className="stat-label">Income {selectedRange.label}</div>
        </div>
        <div className={`stat-card finance-card ${monthTotals.profit >= 0 ? 'success' : 'alert'}`}>
          <div className="stat-value">{formatMoney(monthTotals.profit)}</div>
          <div className="stat-label">Net {selectedRange.label}</div>
        </div>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="section-header">
            <span className="section-title">{editing ? 'Edit Transaction' : 'Log Transaction'}</span>
            <button className="btn btn-ghost btn-sm" onClick={cancel}>Close</button>
          </div>
          <div className="form-grid form-grid-3">
            <div className="field">
              <label>Type</label>
              <select value={form.transaction_type} onChange={e => set('transaction_type', e.target.value)}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                {categories.map(category => <option key={category} value={category}>{category}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Amount ({currencyCode()})</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
            </div>
            <div className="field">
              <label>Vendor / Buyer</label>
              <input value={form.vendor} onChange={e => set('vendor', e.target.value)} placeholder="Name" />
            </div>
            <div className="field">
              <label>Description</label>
              <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="e.g. Hay bales" />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes" />
            </div>
          </div>
          <div className="form-actions mt-3">
            <button className="btn btn-secondary" onClick={cancel}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>{editing ? 'Save Changes' : 'Save Transaction'}</button>
          </div>
        </div>
      )}

      <div className="dashboard-grid mb-4">
        <div className="card chart-card">
          <div className="section-header">
            <span className="section-title">Cash Flow</span>
            <span className="section-note">{selectedRange.label}</span>
          </div>
          <div className="mobile-chart-frame">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2f7d4c" stopOpacity={0.32}/>
                  <stop offset="95%" stopColor="#2f7d4c" stopOpacity={0.02}/>
                </linearGradient>
                <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#b54735" stopOpacity={0.26}/>
                  <stop offset="95%" stopColor="#b54735" stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#d7e0d5" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Area type="monotone" dataKey="income" stroke="#2f7d4c" fill="url(#incomeFill)" strokeWidth={2} animationDuration={850} />
              <Area type="monotone" dataKey="expenses" stroke="#b54735" fill="url(#expenseFill)" strokeWidth={2} animationDuration={850} />
            </AreaChart>
          </ResponsiveContainer>
          </div>
        </div>

        <div className="card chart-card">
          <div className="section-header">
            <span className="section-title">Expense Categories</span>
            <span className="section-note">{selectedRange.label}</span>
          </div>
          {categoriesData.length === 0 ? (
            <div className="empty-chart">No expenses logged for {selectedRange.emptyLabel}.</div>
          ) : (
            <div className="mobile-chart-frame">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoriesData} dataKey="total" nameKey="category" innerRadius={56} outerRadius={92} paddingAngle={3} animationDuration={900}>
                  {categoriesData.map((entry, index) => <Cell key={entry.category} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-4 valuation-card">
        <div className="section-header">
          <span className="section-title">Flock Valuation</span>
          <span className="section-note">{valuationRows.length} of {valuation.rows.length} sheep</span>
        </div>

        <div className="valuation-summary">
          <div>
            <span>Current Flock Value</span>
            <strong>{formatMoney(valuation.estimatedValue)}</strong>
          </div>
          <div>
            <span>Total Income (All Time)</span>
            <strong>{formatMoney(valuation.totalIncome)}</strong>
          </div>
          <div>
            <span>Total Expenses (All Time)</span>
            <strong>{formatMoney(valuation.totalExpenses)}</strong>
          </div>
          <div className={valuation.currentPosition >= 0 ? 'positive' : 'negative'}>
            <span>Current Financial Position</span>
            <strong>{formatMoney(valuation.currentPosition, { showPositiveSign: true })}</strong>
          </div>
          <div>
            <span>Active Flock Purchase Cost</span>
            <strong>{formatMoney(valuation.purchaseCost)}</strong>
          </div>
          <div>
            <span>Missing Values</span>
            <strong>{valuation.missingValues}</strong>
          </div>
        </div>

        <p className="finance-reconciliation-note">
          Livestock purchases are already included in total expenses. Purchase cost is shown for reference and is not subtracted again.
        </p>

        <div className="valuation-tools">
          <input
            className="search-input"
            value={valuationSearch}
            onChange={e => setValuationSearch(e.target.value)}
            placeholder="Search sheep ID..."
          />
          <span className="section-note">Position = current flock value + all income − all expenses</span>
        </div>

        {valuation.rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🐑</div>
            <p>No sheep records yet.</p>
          </div>
        ) : valuationRows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>No sheep match that ID.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="valuation-table">
              <thead>
                <tr>
                  <th>Tag ID</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Purchase Price</th>
                  <th>Sale Price</th>
                  <th>Market Value</th>
                  <th>Used Value</th>
                  <th>Gross P/L</th>
                  <th>Health Cost</th>
                  <th>Net Position</th>
                </tr>
              </thead>
              <tbody>
                {valuationRows.map(row => (
                  <tr key={row.id}>
                    <td data-label="Tag ID"><strong>{row.tag_id}</strong></td>
                    <td data-label="Type"><span className={`badge badge-${row.sex}`}>{row.sex}</span></td>
                    <td data-label="Status"><span className={`badge badge-${row.status}`}>{row.status}</span></td>
                    <td data-label="Purchase Price">{formatMoney(row.purchasePrice)}</td>
                    <td data-label="Sale Price">{row.salePrice != null ? formatMoney(row.salePrice) : '—'}</td>
                    <td data-label="Market Value">
                      {row.salePrice != null ? (
                        <span className="text-muted">Sale price used</span>
                      ) : (
                        <div className="market-value-control">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={marketInputs[row.id] ?? ''}
                            onChange={e => setMarketInputs(inputs => ({ ...inputs, [row.id]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveMarketValue(row); }}
                            placeholder="KES"
                          />
                          <button
                            className="btn btn-secondary btn-sm"
                            type="button"
                            onClick={() => saveMarketValue(row)}
                            disabled={savingMarketId === row.id}
                          >
                            {savingMarketId === row.id ? 'Saving' : 'Save'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td data-label="Used Value">
                      <span className={`value-source ${row.valueSource.toLowerCase()}`}>{row.valueSource}</span>
                      {row.usedValue != null ? formatMoney(row.usedValue) : '—'}
                    </td>
                    <td data-label="Gross P/L">
                      {row.grossProfit != null ? (
                        <strong className={row.grossProfit >= 0 ? 'text-success' : 'text-danger'}>
                          {formatMoney(row.grossProfit, { showPositiveSign: true })}
                        </strong>
                      ) : '—'}
                    </td>
                    <td data-label="Health Cost">{formatMoney(row.healthCost)}</td>
                    <td data-label="Net Position">
                      {row.netPosition != null ? (
                        <strong className={row.netPosition >= 0 ? 'text-success' : 'text-danger'}>
                          {formatMoney(row.netPosition, { showPositiveSign: true })}
                        </strong>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="section-header">
          <span className="section-title">Ledger</span>
          <span className="section-note">{transactions.length} entries</span>
        </div>

        {transactions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📒</div>
            <p>No transactions logged for {selectedRange.emptyLabel}.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Vendor / Buyer</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(t => (
                  <tr key={t.id}>
                    <td data-label="Date">{t.date}</td>
                    <td data-label="Type"><span className={`badge badge-${t.transaction_type}`}>{t.transaction_type}</span></td>
                    <td data-label="Category">{t.category}</td>
                    <td data-label="Description">{t.description || '—'}</td>
                    <td data-label="Vendor / Buyer">{t.vendor || '—'}</td>
                    <td data-label="Amount"><strong className={t.transaction_type === 'income' ? 'text-success' : 'text-danger'}>{formatMoney(t.amount)}</strong></td>
                    <td data-label="Actions">
                      {t.auto_generated ? (
                        <span className="text-muted" style={{ fontSize: 12 }} title="Managed from the sheep record">🔗 Auto</span>
                      ) : (
                        <div className="row-actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => startEdit(t)}>Edit</button>
                          <button className="btn btn-danger btn-sm btn-icon" onClick={() => window.confirm('Delete this transaction?') && remove(t.id)}>✕</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
